if (!customElements.get('countdown-timer')) {
  // Add CSS for animation
  const style = document.createElement('style');
  style.textContent = `
    .countdown-timer-days.animate,
    .countdown-timer-hours.animate,
    .countdown-timer-minutes.animate,
    .countdown-timer-seconds.animate {
      display: inline-block;
      transform: scale(1.2);
      transition: transform 0.2s ease-in-out;
    }
  `;
  document.head.appendChild(style);

  customElements.define(
    'countdown-timer',
    class CountdownTimer extends HTMLElement {
      constructor() {
        super();

        // Read timer type - respect explicit setting, otherwise auto-detect
        this.timer_type = this.getAttribute('timer-type');
        if (!this.timer_type) {
          // Auto-detect: if timer-target exists, it's target mode, otherwise duration
          this.timer_type = this.hasAttribute('timer-target') ? 'target' : 'duration';
        }

        // Handle timer-animate attribute
        var animateAttr = this.getAttribute('timer-animate');
        if (animateAttr === null || animateAttr === undefined || animateAttr === '') {
          this.enableAnimation = true; // Default to true if not specified
        } else {
          var animateAttrLower = String(animateAttr).toLowerCase().trim();
          this.enableAnimation = animateAttrLower === 'true';
        }
        // Handle timer-save-state: can be "true", "false", or boolean
        var saveStateAttr = this.getAttribute('timer-save-state');
        if (saveStateAttr !== null) {
          this.save_state = saveStateAttr === 'true' || saveStateAttr === true;
        } else {
          this.save_state = undefined;
        }
        this.on_finish = this.hasAttribute('timer-on-finish') ? this.getAttribute('timer-on-finish') : undefined;
        this.originalText = this.innerText;
        this.timer = null;

        // Parse target date - if no timezone specified, treat as UTC for global consistency
        if (this.hasAttribute('timer-target')) {
          var targetString = this.getAttribute('timer-target');
          // If format is YYYY-MM-DD HH:MM:SS without timezone, append UTC timezone
          if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(targetString)) {
            targetString = targetString.replace(' ', 'T') + 'Z';
          }
          // If format is YYYY-MM-DD without time, treat as UTC midnight
          else if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(targetString)) {
            targetString = targetString + 'T00:00:00Z';
          }
          // ISO 8601 format with timezone (YYYY-MM-DDTHH:MM:SS±HH:MM) is already valid, use as-is
          // Examples: 2025-02-14T00:00:00-05:00, 2025-02-14T00:00:00+09:00
          this.target_date = new Date(targetString);
        } else {
          this.target_date = null;
        }

        // Initialize duration based on timer type
        if (this.timer_type === 'target' && this.target_date) {
          // Target mode: calculate from target date
          var now = new Date();
          var remainingTime = Math.floor((this.target_date - now) / 1000);
          this.duration = remainingTime > 0 ? remainingTime : 0;
        } else if (this.timer_type === 'duration') {
          // Duration mode: use timer-duration attribute or load from localStorage
          this.duration = this.hasAttribute('timer-duration') ? parseInt(this.getAttribute('timer-duration')) : 0;

          // Load saved state only for duration mode and if save_state is enabled
          if (this.save_state === true && localStorage.getItem('countdownState')) {
            try {
              var savedState = JSON.parse(localStorage.getItem('countdownState'));
              if (savedState && savedState.duration) {
                this.duration = savedState.duration;
              }
            } catch (e) {
              // Invalid saved state, ignore
            }
          }
        } else {
          // Fallback: try to get duration from attribute
          this.duration = this.hasAttribute('timer-duration') ? parseInt(this.getAttribute('timer-duration')) : 0;
        }

        // Render initial state
        this.render();
      }

      connectedCallback() {
        this.start();
      }

      disconnectedCallback() {
        clearInterval(this.timer);
      }

      start() {
        clearInterval(this.timer);

        // Recalculate initial duration if using target mode (in case time passed)
        if (this.timer_type === 'target' && this.target_date) {
          var now = new Date();
          var remainingTime = Math.floor((this.target_date - now) / 1000);
          this.duration = remainingTime > 0 ? remainingTime : 0;
          this.render();
        }

        this.timer = setInterval(() => {
          if (this.timer_type === 'target' && this.target_date) {
            // Target mode: recalculate duration from target date
            var now = new Date();
            var remainingTime = Math.floor((this.target_date - now) / 1000);
            if (remainingTime > 0) {
              this.duration = remainingTime;
            } else {
              this.duration = 0;
            }
          } else {
            // Duration mode: decrement
            this.duration--;
          }

          if (this.duration <= 0) {
            clearInterval(this.timer);
            this.clearState();
            this.onComplete();
          } else {
            // Save state only in duration mode
            if (this.timer_type === 'duration') {
              this.saveState();
            }
            this.render();
          }
        }, 1000);
      }

      saveState() {
        // Only save if save_state is explicitly enabled
        if (this.save_state === true) {
          var state = {
            duration: this.duration,
          };
          localStorage.setItem('countdownState', JSON.stringify(state));
        }
      }

      clearState() {
        localStorage.removeItem('countdownState');
      }

      render() {
        // Ensure duration is valid
        if (this.duration === undefined || this.duration === null || isNaN(this.duration)) {
          this.duration = 0;
        }

        var days = Math.floor(this.duration / (24 * 60 * 60));
        var hours = Math.floor((this.duration % (24 * 60 * 60)) / (60 * 60));
        var minutes = Math.floor((this.duration % (60 * 60)) / 60);
        var seconds = Math.floor(this.duration % 60);

        this.updateSpan('countdown-timer-days', days);
        this.updateSpan('countdown-timer-hours', hours);
        this.updateSpan('countdown-timer-minutes', minutes);
        this.updateSpan('countdown-timer-seconds', seconds);
      }

      updateSpan(className, value) {
        var span = this.querySelector('.' + className);
        if (span) {
          // Remove animate class if animation is disabled
          if (!this.enableAnimation && span.classList) {
            span.classList.remove('animate');
          }

          // Ensure value is valid
          if (value === undefined || value === null || isNaN(value)) {
            value = 0;
          }
          var formattedValue = value > 9 ? value.toString() : '0' + value;
          var hasChanged = span.innerText && span.innerText !== formattedValue;

          // Update value
          if (!span.innerText || span.innerText === '00' || hasChanged) {
            // Add animate class only if enabled and value changed
            if (this.enableAnimation && hasChanged) {
              span.classList.add('animate');
              setTimeout(function () {
                if (span && span.classList) {
                  span.classList.remove('animate');
                }
              }, 200);
            }
            span.innerText = formattedValue;
          }
        }
      }

      onComplete() {
        if (this.on_finish) {
          this.setText(this.on_finish);
        } else {
          this.start();
        }
      }

      setText(text) {
        this.innerHTML = `<div>${text}</div>`;
        this.clearState();
      }
    },
  );
}
